import { z } from 'zod'

export const MailDnsRecordState = z.enum(['ok', 'wrong', 'missing', 'unknown'])

export type MailDnsRecordState = z.infer<typeof MailDnsRecordState>

// `present` says our record is published (the auto-configure writer reads it);
// `state` is the verdict shown to the customer. The fields after `present` are
// absent from an older backend. `purpose` and `issue` stay open strings so an
// unknown code renders generically.
export const MailDnsRecord = z.object({
  type: z.string(),
  name: z.string(),
  value: z.string(),
  ttl: z.number(),
  present: z.boolean(),
  purpose: z.string().optional(),
  required: z.boolean().optional(),
  host: z.string().optional(),
  content: z.string().optional(),
  priority: z.number().optional(),
  weight: z.number().optional(),
  port: z.number().optional(),
  state: MailDnsRecordState.optional(),
  issue: z.string().optional(),
  found: z.array(z.string()).optional(),
  // The single SPF or DMARC record to publish in place of `found`.
  suggested: z.string().optional()
})

export type MailDnsRecord = z.infer<typeof MailDnsRecord>

export const MailDnsStatus = z.object({
  records: z.array(MailDnsRecord),
  status: z.enum(['configured', 'partial', 'missing']),
  provisioned: z.boolean(),
  auto_configurable: z.boolean().optional(),
  // Set while a domain registered elsewhere waits for its _itsh-mail record.
  verification_required: z.boolean().optional()
})

export type MailDnsStatus = z.infer<typeof MailDnsStatus>

export const MailVerifyResult = z.object({
  verified: z.boolean(),
  provisioned: z.boolean()
})

export type MailVerifyResult = z.infer<typeof MailVerifyResult>

export const MailProvisionRequest = z.object({
  enable: z.boolean().optional()
})

export type MailProvisionRequest = z.infer<typeof MailProvisionRequest>

export const MailProvisionResponse = z.object({
  dkim_record: z.string().optional(),
  dkim_selector: z.string().optional()
})

export type MailProvisionResponse = z.infer<typeof MailProvisionResponse>

export const MailCatchall = z.object({
  enabled: z.boolean(),
  target_mailbox_id: z.number().optional(),
  target_email: z.string().optional()
})

export type MailCatchall = z.infer<typeof MailCatchall>

export const UpdateCatchallRequest = z.object({
  target_mailbox_id: z.number()
})

export type UpdateCatchallRequest = z.infer<typeof UpdateCatchallRequest>
